import { useState } from "react";
import PropTypes from "prop-types";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { toast } from "react-toastify";
import ConfirmDeleteModal from "../ConfirmDeleteModal/ConfirmDeleteModal";

const InlineDeleteButton = styled.button`
	background-color: rgb(202, 91, 79);
	color: white;
	border: none;
	border-radius: 10rem;
	padding: 0.5rem 1.2rem;
	font-size: 1.2rem;
	font-weight: 500;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 1.2;
	height: 2.4rem;

	&:hover {
		background-color: #c0392b;
	}

	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.4);
	}
`;

export default function DeleteDecisionButton({ decisionId, onDeleted }) {
	const { axiosPrivate } = useAxiosPrivate();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [modalOpen, setModalOpen] = useState(false);

	const handleDelete = async () => {
		try {
			await axiosPrivate.delete(`/decision/maker/${parseInt(decisionId)}.json`);
			// Invalidate and refetch decisions-related queries so lists refresh
			try {
				await queryClient.invalidateQueries({
					predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "decisionsList",
				});
				await queryClient.invalidateQueries({
					predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "decision-id",
				});
				await queryClient.refetchQueries({
					predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "decisionsList",
				});
			} catch (e) {
				// Non-fatal: cache will refresh on next view/mount
				console.warn("Decisions cache invalidation failed", e);
			}
			if (onDeleted) onDeleted();
			navigate("/decisions", { replace: true });
		} catch (error) {
			console.error("Failed to delete decision:", error);
			
			// Check if the error is related to foreign key constraints (directions)
			const errorMessage = error?.response?.data?.message || error?.message || "";
			const isConstraintError = errorMessage.includes("foreign key constraint") || 
									 errorMessage.includes("Cannot delete") ||
									 errorMessage.includes("constraint fails") ||
									 error?.response?.status === 500;
			
			if (isConstraintError) {
				toast.error(
					"Cannot delete this decision because it has directions associated with it. " +
					"Please delete all directions from the 'Directions' tab first, then try again.",
					{
						autoClose: 8000,
						closeOnClick: true
					}
				);
			} else {
				toast.error("Failed to delete decision. Please try again or contact support.");
			}
		} finally {
			setModalOpen(false);
		}
	};

	return (
		<>
			<InlineDeleteButton onClick={() => setModalOpen(true)}>
				Delete
			</InlineDeleteButton>

			<ConfirmDeleteModal
				isOpen={modalOpen}
				onRequestClose={() => setModalOpen(false)}
				onConfirm={handleDelete}
				title="Confirm deletion of decision"
				message={`You are about to delete all data linked to this decision.\n
				This means that this decision will no longer be available for history or follow-up.\n
				Do you want to proceed?`}
				confirmLabel="Yes, delete decision"
			/>
		</>
	);
}

DeleteDecisionButton.propTypes = {
	decisionId: PropTypes.number.isRequired,
	onDeleted: PropTypes.func,
};